import type { Ref } from "react";
import { CheckCircle2 } from "lucide-react";
import { Badge, Card } from "@repo/ui";
import {
  ATTENTES,
  intervalleDeAttente,
  LIBELLE_ATTENTE,
  libelleZone,
  type Echelle,
} from "@convex/referentiel/fourchette";
import {
  definitionTrait,
  estComplete,
  zoneDe,
  type Dimension,
  type ValeurDimension,
} from "./fourchettes-modele";
import { RepartitionZone } from "./repartition-zone";

type ChoixProps<T extends string> = {
  nom: string;
  /** Intitulé court, visible à gauche des options. */
  intitule: string;
  /** Question complète, lue par les technologies d'assistance. */
  legende: string;
  options: readonly T[];
  libelle: (option: T) => string;
  detail?: (option: T) => string | undefined;
  valeur: T | undefined;
  lectureSeule: boolean;
  onChange: (option: T) => void;
};

/** Colonnes des options : 2 × 2 en largeur moyenne, une ligne de 4 en grande.
 *  Classes littérales, sinon Tailwind ne les génère pas. */
const COLONNES: Record<number, string> = {
  2: "grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

/** Groupe de choix exclusifs. Des boutons radio plutôt qu'un `select` : toutes
 *  les options doivent être lisibles SANS ouvrir quoi que ce soit, c'est ce
 *  qui rend l'arbitrage possible d'un coup d'œil.
 *
 *  L'intitulé est court et posé en colonne à gauche : une question longue
 *  au-dessus de chaque groupe faisait jeu égal avec le nom de la dimension. La
 *  question complète reste dans la `legend`, pour les lecteurs d'écran. */
function GroupeChoix<T extends string>({
  nom,
  intitule,
  legende,
  options,
  libelle,
  detail,
  valeur,
  lectureSeule,
  onChange,
}: ChoixProps<T>) {
  return (
    <fieldset>
      <legend className="sr-only">{legende}</legend>
      <div className="grid gap-x-6 gap-y-2 md:grid-cols-[8.5rem_1fr]">
        <span aria-hidden className="text-sm text-charte-dim md:pt-2.5">
          {intitule}
        </span>
        <div className={`grid gap-2 ${COLONNES[options.length] ?? ""}`}>
          {options.map((option) => {
            const choisi = valeur === option;
            const sousTexte = detail?.(option);
            return (
              <label
                key={option}
                className={`flex items-center gap-2.5 rounded-surface border px-3 py-2 text-sm transition-colors duration-150 ${
                  choisi
                    ? "border-primary bg-primary/5 text-charte-strong"
                    : "border-charte-line-strong text-charte-soft"
                } ${
                  lectureSeule
                    ? "cursor-default"
                    : choisi
                      ? "cursor-pointer"
                      : "cursor-pointer hover:border-charte-line-loud hover:bg-charte-hover"
                } ${lectureSeule && !choisi ? "opacity-60" : ""}`}
              >
                <input
                  type="radio"
                  className="accent-primary"
                  name={nom}
                  checked={choisi}
                  disabled={lectureSeule}
                  onChange={() => onChange(option)}
                />
                <span className="min-w-0">
                  <span className={`block ${choisi ? "font-semibold" : "font-medium"}`}>
                    {libelle(option)}
                  </span>
                  {sousTexte && (
                    <span className="block text-xs tabular-nums text-charte-dim">
                      {sousTexte}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </fieldset>
  );
}

type Props = {
  dimension: Dimension;
  valeur: ValeurDimension;
  echelle: Echelle;
  lectureSeule: boolean;
  /** Titre de la dimension : reçoit le focus quand la carte arrive. */
  titreRef: Ref<HTMLHeadingElement>;
  onModifier: (champ: ValeurDimension) => void;
};

/**
 * Une dimension du référentiel PAMI (§7.1) : UNE question, aucune borne à
 * régler — l'ATTENTE du poste sur ce trait : score bas (0 à 3), moyen (4 à 6),
 * élevé (7 à 10), ou pas d'attente particulière (hors du score).
 *
 * Sous le nom du trait : sa définition, puis ce que signifient un score bas et
 * un score élevé (textes du référentiel PAMI, `definitionTrait`). Le référent
 * choisit une zone sur une échelle qu'il doit pouvoir lire sans le rapport.
 *
 * La zone est écrite sous chaque option et dessinée sur la cloche de
 * l'étalonnage, pour que le référent voie la part de la population qu'il
 * retient.
 */
export function CarteDimension({
  dimension,
  valeur,
  echelle,
  lectureSeule,
  titreRef,
  onModifier,
}: Props) {
  const complete = estComplete(valeur);
  const definition = definitionTrait(dimension.code);
  // La cloche est celle de l'étalonnage 0-10 : sur une autre échelle, elle ne
  // dirait rien de la zone.
  const estEchellePami = echelle.min === 0 && echelle.max === 10;

  return (
    <Card className={`gap-0 py-0 ${complete ? "" : "border-charte-line-loud"}`}>
      <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-3">
        <div className="min-w-0">
          <h4
            ref={titreRef}
            tabIndex={-1}
            className="text-lg font-semibold leading-snug text-charte-strong outline-none"
          >
            {dimension.label}
          </h4>
          {dimension.description && (
            <p className="text-sm text-charte-dim">{dimension.description}</p>
          )}
        </div>
        {complete ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 pt-1 text-sm font-medium text-etat-fait-contenu">
            <CheckCircle2 aria-hidden className="h-4 w-4 text-etat-fait" />
            Renseignée
          </span>
        ) : (
          <Badge variant="outline" className="mt-0.5 shrink-0">
            À renseigner
          </Badge>
        )}
      </div>

      {definition && (
        <div className="space-y-3 px-5 pb-4">
          <p className="text-sm text-charte-soft">{definition.definition}</p>
          {/* Bas à gauche, élevé à droite : le même sens que les options
              juste en dessous. Un simple filet, pas un cadre : un encadré
              arrondi se lirait comme une option de plus à cliquer. */}
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div className="border-l-2 border-charte-line-loud pl-3">
              <dt className="font-medium text-charte-strong">Score bas</dt>
              <dd className="text-charte-muted">{definition.bas}</dd>
            </div>
            <div className="border-l-2 border-charte-line-loud pl-3">
              <dt className="font-medium text-charte-strong">Score élevé</dt>
              <dd className="text-charte-muted">{definition.haut}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="space-y-3 border-t border-charte-line px-5 py-4">
        <GroupeChoix
          nom={`attente-${dimension.code}`}
          intitule="Attente"
          legende="Score attendu chez le candidat sur cette dimension"
          options={ATTENTES}
          libelle={(attente) => LIBELLE_ATTENTE[attente]}
          detail={(attente) =>
            attente === "aucune"
              ? "hors du score"
              : `score ${libelleZone(intervalleDeAttente(attente, echelle))}`
          }
          valeur={valeur.attente}
          lectureSeule={lectureSeule}
          onChange={(attente) => onModifier({ attente })}
        />

        {estEchellePami && (
          <div className="pt-2">
            <RepartitionZone
              zone={zoneDe(valeur, echelle)}
              sansAttente={valeur.attente === "aucune"}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
